import {
  Box,
  Paper,
  Skeleton,
  Stack,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import ShowContainer from '../../../components/material-ui/ShowContainer';
import PlayersPivot from './PlayersPivot';
import Table from '../../../components/material-ui/adaptive/Table';
import { useTranslation } from 'react-i18next';
import MyTableCell from '../../../components/material-ui/adaptive/MyTableCell';
import MyTableBoldCell from '../../../components/material-ui/adaptive/MyTableBoldCell';
import TypographyTitle from '../../../components/material-ui/table/TypographyTitle';
import { ENUM_DEPOSIT_STATUS, ENUM_INVOICE_ACTIONS, ENUM_PAYOUT_TYPE } from '../../../enums/enums';
import TypographyText from '../../../components/material-ui/table/TypographyText';
import TypographyLink from '../../../components/material-ui/table/TypographyLink';
import { getOnlyData } from '../../../utils';
import dayjs from 'dayjs';
import PaymentGatewayReplies from './modals/PaymentGatewayReplies';
import { API_FINANCE } from '../../../api/finance';
import useToast from '../../../hooks/useToast';
import PaymentRejectModal from './modals/PaymentRejectModal';
import PaymentMakeModal from './modals/PaymentMakeModal';
import { useDispatch } from 'react-redux';
import ErrorBoundary from '../../../components/material-ui/error_boundary/ErrorBoundary';
import CusLink from '../../../components/material-ui/CusLink';

// payoutInfo.payouts
// 0 - идентификатор заявки в БД
// 1 - идентификатор пользователя
// 2 - сумма заявки
// 3 - сумма комиссии казино
// 4 - сумма выплаты
// 5 - 3-символьный код валюты заявки
// 6 - числовой код текущего статуса заявки
// 7 - дата создания заявки в формате 'YYYY-MM-DD hh:mm:ss'
// 8 - дата последнего изменения статуса в формате 'YYYY-MM-DD hh:mm:ss'
// 9 - тип вывода ('card', 'phone', 'crypto' и т.д.)
// 10 - номер карты/кошелька
// 11 - получатель (ФИО)
// 12 - комментарий
// 13 - адрес страницы оплаты
// 14 - метод оплаты. Для новой заявки (в статусе ‘created’) - пустая строка. Для выплаты в
//        ручном режиме - 'manual'. Для выплаты через шлюз - код шлюза ('sg', ‘exp’ и т.п.)
// 15 - идентификатор заявки в платежной системе (ордер, транзакция и т.п.).
// 16 - адрес вывода для типа 'crypto'
// 17 - номер телефона (для типа 'phone')
// 18 - название банка (для типа 'phone')
// 19 - сумма комиссии шлюза, через который была выплата
// 20 - массив доступных операций ENUM_INVOICE_ACTIONS

export default function PayoutPlayersTable({
  updateGates,
  payoutInfo,
  alreadyShow,
  btnLoading,
  startPageNumber,
  statusHash
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { successToast } = useToast();
  const {
    reactId,
    groupList: groupMap = [],
    list: defPayouts = [],
    userList: userMap = [],
    casinoList: casinoMap = []
  } = payoutInfo;

  const [payouts, setPayouts] = useState([]);
  const [openRawId, setOpenRawId] = useState(null);
  const [openRejectId, setOpenRejectId] = useState(null);
  const [openPayoutId, setOpenPayoutId] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);

  const updateInfo = (id, info = {}) => {
    const { status, method, changed, comment, actions, transId } = info;
    setPayouts((prev) =>
      prev.map((el) => {
        if (el[0] == id) {
          const prevInfo = [...el];
          if (status) prevInfo[6] = status;
          if (method) prevInfo[14] = method;
          if (changed) prevInfo[8] = changed;
          if (comment) prevInfo[12] = comment;
          if (actions) prevInfo[20] = actions;
          if (transId) prevInfo[15] = transId;
          return prevInfo;
        }
        return el;
      })
    );
  };

  useEffect(() => {
    setPayouts(defPayouts);
  }, [reactId]);

  const checkStatus = async (id) => {
    setLoadingStatus(true);
    API_FINANCE.payout
      .statusCheck({ invoiceId: id })
      .then((e) => {
        updateInfo(id, e.data.data);
        successToast('Status successfully updated');
      })
      .catch(console.log)
      .finally(() => {
        setLoadingStatus(false);
      });
  };

  const getViewDataTime = (dateTime) => {
    return getOnlyData(dayjs(dateTime), '', 'DD.MM.YYYY HH:mm');
  };

  const casinoId = useMemo(() => {
    const id = payouts.find((el) => el[0] == openPayoutId)?.[1];
    if (!id) return;

    const userInfo = userMap[+id] || [];
    const group = groupMap[userInfo[1]] || [];
    const casinoId = group[1];

    return +casinoId;
  }, [openPayoutId]);

  // const gatesNameByCode = gates.reduce((acc, item) => {
  //   acc[item.code] = item.label;
  //   return acc;
  // }, {});

  return (
    <>
      <Stack gap={4}>
        <ShowContainer
          condition={payouts.length}
          plug={
            <ShowContainer condition={alreadyShow && !btnLoading}>
              <Box
                sx={{
                  background: (theme) => theme.palette.grey[200],
                  color: (th) => th.palette.text.secondary,
                  borderRadius: 1,
                  p: 3
                }}>
                <Typography fontSize={16} fontWeight={600}>
                  {t('No records found. Please update your filters.')}
                </Typography>
              </Box>
            </ShowContainer>
          }>
          <PlayersPivot payouts={payouts} />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <MyTableBoldCell sx={{ p: 1, pr: 0 }}>#</MyTableBoldCell>
                  <MyTableBoldCell sx={{ p: 1, pr: 0 }}>{t('Invoice Id')}</MyTableBoldCell>
                  <MyTableBoldCell isLess width={'140px'}>
                    {t('User')}
                  </MyTableBoldCell>
                  <MyTableBoldCell isLess>{t('Sum')}</MyTableBoldCell>
                  <MyTableBoldCell isLess>{t('Payout')}</MyTableBoldCell>
                  <MyTableBoldCell isLess>{t('Invoice status')}</MyTableBoldCell>
                  <MyTableCell isLess>
                    <Stack>
                      <TypographyTitle>{t('Date Created')}/</TypographyTitle>
                      <TypographyTitle>{t('Modified')}</TypographyTitle>
                    </Stack>
                  </MyTableCell>
                  <MyTableBoldCell isLess>{t('Comment')}</MyTableBoldCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payouts.map((info, indx) => {
                  const userId = +info[1];
                  const userInfo = userMap[userId] || [];
                  const group = groupMap[userInfo[1]] || [];
                  const groupName = group[0];
                  const currency = info[5];
                  const statusName = ENUM_DEPOSIT_STATUS[statusHash[info[6]]];
                  const groupId = userInfo[1];
                  const userName = userInfo[0];
                  const casinoId = group[1];
                  const type = info[9];
                  const rulesList = info[20] || [];
                  // const methodName = gatesNameByCode[info[14]] || t(info[14]);
                  const methodName = t(info[14]);
                  const orderId = info[15];

                  const sumFields = [
                    { name: 'Invoice sum', value: info[2] },
                    { name: 'Our commission', value: info[3] },
                    { name: 'Gateway commission', value: info[19] },
                    { name: 'To be paid', value: info[4] }
                  ];
                  const payoutFields = [];
                  const actionFields = [];

                  //ENUM_PAYOUT_TYPE
                  if (type == ENUM_PAYOUT_TYPE.phone) {
                    payoutFields.push({ name: 'To the phone', value: info[17] });
                    payoutFields.push({ name: 'Bank name', value: info[18] });
                  }
                  if (type == ENUM_PAYOUT_TYPE.card) {
                    payoutFields.push({ name: 'To the card', value: info[10] });
                  }
                  if (type == ENUM_PAYOUT_TYPE.acc) {
                    payoutFields.push({ name: 'To the card', value: info[10] });
                  }
                  if (type == ENUM_PAYOUT_TYPE.crypto) {
                    payoutFields.push({ type: 'column', name: 'Crypto wallet', value: info[16] });
                  }

                  const id = info[0];
                  //ENUM_INVOICE_ACTIONS
                  if (rulesList.includes(ENUM_INVOICE_ACTIONS.checkStatus)) {
                    actionFields.push({ name: 'Check status', onClick: () => checkStatus(id) });
                  }
                  if (rulesList.includes(ENUM_INVOICE_ACTIONS.makePayout)) {
                    actionFields.push({
                      name: 'Make a payment',
                      onClick: () => setOpenPayoutId(id)
                    });
                  }
                  if (rulesList.includes(ENUM_INVOICE_ACTIONS.makeReject)) {
                    actionFields.push({ name: 'Reject', onClick: () => setOpenRejectId(id) });
                  }
                  if (rulesList.includes(ENUM_INVOICE_ACTIONS.viewRaw)) {
                    actionFields.push({
                      name: 'Gateway replies',
                      onClick: () => setOpenRawId(id)
                    });
                  }

                  return (
                    <TableRow key={id}>
                      <MyTableCell sx={{ p: 1, pr: 0 }}>{startPageNumber + indx}</MyTableCell>
                      <MyTableCell sx={{ p: 1, pr: 0 }} width="50px">
                        {id}
                      </MyTableCell>
                      <MyTableCell isLess>
                        <Stack gap={0.5} sx={{ maxWidth: '150px' }}>
                          <TypographyText>
                            {userId}:{' '}
                            <TypographyText
                              isCopy={userInfo[0]}
                              sx={{ wordBreak: 'break-all', p: 0 }}>
                              {userName}
                            </TypographyText>
                          </TypographyText>

                          <CusLink text="User log" to={`/users/logs/${userId}`} />
                          <CusLink text="IP address" to={`/users/IpAddresses/${userId}`} />

                          <CusLink
                            text={'Financial profile'}
                            to={`/users/finance/${userId}/${currency}`}
                          />

                          <TypographyText>
                            {t('Group')}: <CusLink text={groupName} to={`/groups/${groupId}`} />
                          </TypographyText>

                          <TypographyText>
                            {t('Casino')}:{' '}
                            <CusLink
                              text={casinoMap[casinoId]}
                              to={`/settings/casino/${casinoId}`}
                            />
                          </TypographyText>
                        </Stack>
                      </MyTableCell>
                      <MyTableCell isLess>
                        <Stack>
                          {sumFields.map((el) => (
                            <Stack
                              key={el.name}
                              flexDirection={'row'}
                              justifyContent={'space-between'}
                              gap={1}>
                              <TypographyText color="textSecondary">{t(el.name)}:</TypographyText>
                              <TypographyText fontWeight={600} component="span">
                                {el.value}{' '}
                                <TypographyText
                                  color="textSecondary"
                                  component="span"
                                  fontSize={11}>
                                  {currency}
                                </TypographyText>
                              </TypographyText>
                            </Stack>
                          ))}
                        </Stack>
                      </MyTableCell>
                      <MyTableCell isLess>
                        <Stack sx={{ maxWidth: '180px' }}>
                          {payoutFields.map((el) => (
                            <Stack
                              key={el.name}
                              flexDirection={el.type || 'row'}
                              justifyContent={'space-between'}
                              gap={2}>
                              <TypographyText color="textSecondary">{t(el.name)}:</TypographyText>
                              <TypographyText fontWeight={600}>{el.value}</TypographyText>
                            </Stack>
                          ))}
                          <TypographyText pt={1}>{info[11]}</TypographyText>
                          <ShowContainer condition={orderId}>
                            <Box
                              sx={{
                                display: 'inline',
                                whiteSpace: 'normal',
                                wordWrap: 'break-word'
                              }}>
                              <TypographyText>ID:</TypographyText>
                              <TypographyText sx={{ p: 0 }} isCopy={orderId}>
                                {orderId}
                              </TypographyText>
                            </Box>
                          </ShowContainer>
                        </Stack>
                      </MyTableCell>
                      <MyTableCell isLess>
                        <ShowContainer
                          condition={!loadingStatus}
                          plug={
                            <Skeleton variant="square" animation="wave" width="100%" height={60} />
                          }>
                          <Stack gap={0.5}>
                            <TypographyText>{t(statusName)}</TypographyText>
                            <TypographyText>{methodName}</TypographyText>
                            {actionFields.map((el) => (
                              <TypographyLink key={el.name} onClick={el.onClick}>
                                {t(el.name)}
                              </TypographyLink>
                            ))}
                          </Stack>
                        </ShowContainer>
                      </MyTableCell>
                      <MyTableCell isLess>
                        <Stack>
                          <TypographyText>{getViewDataTime(info[7])} </TypographyText>
                          <TypographyText>{getViewDataTime(info[8])}</TypographyText>
                        </Stack>
                      </MyTableCell>
                      <MyTableCell isLess>{info[12]}</MyTableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </ShowContainer>
      </Stack>

      <ErrorBoundary>
        <PaymentGatewayReplies openRawId={openRawId} setOpenRawId={setOpenRawId} />
      </ErrorBoundary>
      <ErrorBoundary>
        <PaymentRejectModal
          openRejectId={openRejectId}
          setOpenRejectId={setOpenRejectId}
          updateInfo={updateInfo}
          payout={payouts.find((el) => el[0] == openRejectId) || []}
        />
      </ErrorBoundary>
      <ErrorBoundary>
        <PaymentMakeModal
          info={payouts.find((el) => el[0] == openPayoutId) || []}
          openPayoutId={openPayoutId}
          updateGates={updateGates}
          setOpenPayoutId={setOpenPayoutId}
          updateInfo={updateInfo}
          casinoId={casinoId}
        />
      </ErrorBoundary>
    </>
  );
}
